import type { Currency, DocumentType, ExpenseKind, StopStatus, TripEventType } from '@kmdx/contracts';
import { db, metaGet, metaSet } from './db';
import { captureFix } from './geo';
import { newId } from './ids';
import { canvasToPng, processPhoto, type PhotoQuality } from './image';
import { enqueue } from './outbox';
import { sha256OfBlob } from './sha256';
import { requestFlush } from './sync';
import type { EventRecord, ExpenseRecord, OdometerReading, PhotoRecord, VehicleCheckItem } from './types';

/**
 * Всяко действие на шофьора минава оттук: пише се в Dexie, влиза в опашката и
 * чак после (може би) тръгва по мрежата. Нито един екран не чака сървъра.
 */

/**
 * Кои събития дърпат позиция от телефона — петте момента по адреса и
 * подписите по ЧМР (виж `geo.ts`). Позицията на подписа отива в е-ЧМР и в
 * PDF-а: къде е подписал изпращачът, превозвачът и получателят.
 */
const POSITION_EVENTS: TripEventType[] = [
  'arrived_pickup',
  'loaded',
  'departed',
  'arrived_delivery',
  'unloaded',
  'ecmr_signed',
  'pod_captured',
];

/**
 * Товаренето приключва с натоварването. Разтоварването — чак с подписа:
 * без подпис на получателя няма доказателство за доставка, а без него няма
 * фактура. Затова „разтоварих" не затваря адреса.
 */
const STOP_STATUS_BY_EVENT: Partial<Record<TripEventType, StopStatus>> = {
  /** „Тръгвам" се записва срещу адреса, КЪМ който се пътува. */
  started: 'en_route',
  departed: 'en_route',
  arrived_pickup: 'arrived',
  arrived_delivery: 'arrived',
  loaded: 'completed',
  pod_captured: 'completed',
};

export interface RecordEventInput {
  tripId: string;
  orderStopId?: string | null;
  type: TripEventType;
  note?: string | null;
  payload?: Record<string, unknown> | null;
}

/**
 * Записът става МИГНОВЕНО, часът е часът на натискане. Координатата се търси
 * след това и се закача, ако дойде — под рампа GPS-ът мълчи по десет секунди,
 * а шофьорът не бива да гледа въртящо се колело с ръка на бутона.
 */
export async function recordEvent(input: RecordEventInput): Promise<EventRecord> {
  const needsFix = POSITION_EVENTS.includes(input.type);
  const event: EventRecord = {
    id: newId('evt'),
    tripId: input.tripId,
    orderStopId: input.orderStopId ?? null,
    type: input.type,
    at: new Date().toISOString(),
    latE7: null,
    lonE7: null,
    accuracyM: null,
    note: input.note ?? null,
    payload: input.payload ?? null,
    synced: 0,
  };
  await db.events.put(event);
  await enqueue('event', event.id, eventLabel(input.type), needsFix ? FIX_GRACE_MS : 0);

  const nextStatus = STOP_STATUS_BY_EVENT[input.type];
  if (nextStatus && input.orderStopId) {
    await db.stops.update(input.orderStopId, { status: nextStatus });
  }

  if (needsFix) void attachFixToEvent(event.id);
  else requestFlush();
  return event;
}

/** Колко чака опашката координатата, преди да прати събитието без нея. */
const FIX_GRACE_MS = 15_000;

async function attachFixToEvent(eventId: string): Promise<void> {
  const fix = await captureFix(12_000);
  const event = await db.events.get(eventId);
  if (fix && event && event.synced === 0) {
    await db.events.update(eventId, {
      latE7: fix.latE7,
      lonE7: fix.lonE7,
      accuracyM: fix.accuracyM,
    });
  }
  await db.outbox.update(eventId, { nextAttemptAt: 0 });
  requestFlush();
}

async function attachFixToPhoto(photoId: string): Promise<void> {
  const fix = await captureFix(8_000);
  const photo = await db.photos.get(photoId);
  if (fix && photo && photo.status === 'queued') {
    await db.photos.update(photoId, {
      latE7: fix.latE7,
      lonE7: fix.lonE7,
      accuracyM: fix.accuracyM,
    });
  }
  await db.outbox.update(photoId, { nextAttemptAt: 0 });
  requestFlush();
}

export function eventLabel(type: TripEventType): string {
  const labels: Partial<Record<TripEventType, string>> = {
    accepted: 'Приет курс',
    rejected: 'Отказан курс',
    started: 'Тръгване',
    arrived_pickup: 'Пристигане за товарене',
    loaded: 'Натоварено',
    departed: 'Тръгване от адреса',
    arrived_delivery: 'Пристигане за разтоварване',
    unloaded: 'Разтоварено',
    pod_captured: 'Подпис на получателя',
    ecmr_signed: 'Подпис по е-ЧМР',
    delayed: 'Закъснение',
    incident: 'Произшествие',
    completed: 'Приключен курс',
    note: 'Бележка',
  };
  return labels[type] ?? 'Събитие';
}

/** ─── Снимки ──────────────────────────────────────────────────────────── */

export interface SavePhotoInput {
  file: Blob;
  tripId: string | null;
  orderStopId: string | null;
  type: DocumentType;
  quality: PhotoQuality;
  /** Часът, в който шофьорът е потвърдил, че снимката се чете. */
  confirmedAt?: string;
  /** Снимка по задача „снимай пак" — върнатият документ, който замества. */
  replacesDocumentId?: string | null;
  note?: string | null;
}

export async function savePhoto(input: SavePhotoInput): Promise<PhotoRecord> {
  const processed = await processPhoto(input.file, input.quality);
  const sha256 = await sha256OfBlob(processed.blob);

  // Два пъти щракнатата една и съща страница се разпознава по контролната сума.
  const duplicate = await db.photos.where('sha256').equals(sha256).first();
  if (duplicate && duplicate.orderStopId === input.orderStopId) return duplicate;

  const pageCount = await db.photos
    .where('orderStopId')
    .equals(input.orderStopId ?? '')
    .filter((photo) => photo.type === input.type)
    .count();

  const photo: PhotoRecord = {
    id: newId('doc'),
    tripId: input.tripId,
    orderStopId: input.orderStopId,
    type: input.type,
    fileName: `${input.type}_${Date.now()}.jpg`,
    mimeType: 'image/jpeg',
    sizeBytes: processed.blob.size,
    sha256,
    width: processed.width,
    height: processed.height,
    thumbDataUrl: processed.thumbDataUrl,
    blob: processed.blob,
    capturedAt: new Date().toISOString(),
    latE7: null,
    lonE7: null,
    accuracyM: null,
    status: 'queued',
    progress: 0,
    error: null,
    page: pageCount + 1,
    confirmedAt: input.confirmedAt ?? new Date().toISOString(),
    replacesDocumentId: input.replacesDocumentId ?? null,
    note: input.note ?? null,
  };
  await db.photos.put(photo);
  await enqueue('photo', photo.id, `Снимка: ${photo.fileName}`, FIX_GRACE_MS);
  void attachFixToPhoto(photo.id);
  return photo;
}

export async function deletePhoto(id: string): Promise<void> {
  await db.photos.delete(id);
  await db.outbox.delete(id);
}

/** ─── Километраж ──────────────────────────────────────────────────────── */

/**
 * Показанието при тръгване и при връщане — от него е заплатата на километър.
 * Пази се на телефона, докато не тръгне с „Тръгвам" или „Приключи курса"
 * (в полезния товар на събитието); снимката отива отделно, като документ.
 */
const odometerKey = (tripId: string, which: 'start' | 'end') => `odometer:${tripId}:${which}`;

export async function saveOdometer(
  tripId: string,
  which: 'start' | 'end',
  reading: { km: number; docId: string },
): Promise<void> {
  await metaSet(odometerKey(tripId, which), { ...reading, at: new Date().toISOString() } satisfies OdometerReading);
}

export function getOdometer(tripId: string, which: 'start' | 'end'): Promise<OdometerReading | undefined> {
  return metaGet<OdometerReading>(odometerKey(tripId, which));
}

/** ─── Подпис ──────────────────────────────────────────────────────────── */

export interface SignatureInput {
  canvas: HTMLCanvasElement;
  tripId: string;
  orderStopId: string;
  receiverName: string;
  /** Текстът на забележките е доказателството при рекламация за щета. */
  reservations: string | null;
}

export async function saveSignature(input: SignatureInput): Promise<void> {
  const png = await canvasToPng(input.canvas);
  const sha256 = await sha256OfBlob(png);

  const photo: PhotoRecord = {
    id: newId('sig'),
    tripId: input.tripId,
    orderStopId: input.orderStopId,
    type: 'pod',
    fileName: `podpis_${Date.now()}.png`,
    mimeType: 'image/png',
    sizeBytes: png.size,
    sha256,
    width: input.canvas.width,
    height: input.canvas.height,
    thumbDataUrl: input.canvas.toDataURL('image/png'),
    blob: png,
    capturedAt: new Date().toISOString(),
    latE7: null,
    lonE7: null,
    accuracyM: null,
    status: 'queued',
    progress: 0,
    error: null,
    page: 1,
    /** Подписът е потвърден с самото подписване пред шофьора. */
    confirmedAt: new Date().toISOString(),
  };
  await db.photos.put(photo);
  await enqueue('photo', photo.id, 'Подпис на получателя', FIX_GRACE_MS);
  void attachFixToPhoto(photo.id);

  await recordEvent({
    tripId: input.tripId,
    orderStopId: input.orderStopId,
    type: 'pod_captured',
    note: input.reservations,
    payload: {
      receiverName: input.receiverName,
      hasReservations: input.reservations !== null && input.reservations.trim().length > 0,
      reservations: input.reservations,
      signatureDocId: photo.id,
    },
  });
}

/** ─── Електронна ЧМР на товарене ───────────────────────────────────────── */

export interface EcmrLoadingInput {
  tripId: string;
  orderStopId: string;
  sender: { name: string; canvas: HTMLCanvasElement };
  carrier: { name: string; canvas: HTMLCanvasElement };
}

/**
 * Двата подписа на товарене — изпращачът (кл. 22) и шофьорът за превозвача
 * (кл. 23). Всеки подпис е снимка в опашката за качване и събитие с ключа ѝ;
 * сървърът ги връзва към електронната ЧМР на заявката. Работи и без връзка.
 */
export async function saveEcmrLoading(input: EcmrLoadingInput): Promise<void> {
  for (const [role, part] of [
    ['sender', input.sender],
    ['carrier', input.carrier],
  ] as const) {
    const png = await canvasToPng(part.canvas);
    const sha256 = await sha256OfBlob(png);
    const photo: PhotoRecord = {
      id: newId('sig'),
      tripId: input.tripId,
      orderStopId: input.orderStopId,
      type: 'other',
      fileName: `echmr_${role === 'sender' ? 'izprashtach' : 'prevozvach'}_${Date.now()}.png`,
      mimeType: 'image/png',
      sizeBytes: png.size,
      sha256,
      width: part.canvas.width,
      height: part.canvas.height,
      thumbDataUrl: part.canvas.toDataURL('image/png'),
      blob: png,
      capturedAt: new Date().toISOString(),
      latE7: null,
      lonE7: null,
      accuracyM: null,
      status: 'queued',
      progress: 0,
      error: null,
      page: 1,
      confirmedAt: new Date().toISOString(),
    };
    await db.photos.put(photo);
    await enqueue('photo', photo.id, role === 'sender' ? 'Подпис на изпращача' : 'Подпис на превозвача', FIX_GRACE_MS);
    void attachFixToPhoto(photo.id);
    await recordEvent({
      tripId: input.tripId,
      orderStopId: input.orderStopId,
      type: 'ecmr_signed',
      payload: { role, signerName: part.name, signatureDocId: photo.id },
    });
  }
}

/** ─── Разходи ─────────────────────────────────────────────────────────── */

export interface SaveExpenseInput {
  tripId: string | null;
  kind: ExpenseKind;
  amountMinor: number;
  ccy: Currency;
  country: string;
  supplier?: string | null;
  litresX100?: number | null;
  odometerKm?: number | null;
  note?: string | null;
  receiptDocId?: string | null;
}

export async function saveExpense(input: SaveExpenseInput): Promise<ExpenseRecord> {
  const expense: ExpenseRecord = {
    id: newId('exp'),
    tripId: input.tripId,
    kind: input.kind,
    amountMinor: input.amountMinor,
    ccy: input.ccy,
    country: input.country,
    spentAt: new Date().toISOString(),
    supplier: input.supplier ?? null,
    litresX100: input.litresX100 ?? null,
    odometerKm: input.odometerKm ?? null,
    note: input.note ?? null,
    receiptDocId: input.receiptDocId ?? null,
    synced: 0,
  };
  await db.expenses.put(expense);
  await enqueue('expense', expense.id, 'Разход');
  requestFlush();
  return expense;
}

/** ─── Съобщения ───────────────────────────────────────────────────────── */

export async function sendMessage(body: string, tripId: string | null): Promise<void> {
  const message = {
    id: newId('msg'),
    tripId,
    direction: 'out' as const,
    body,
    sentAt: new Date().toISOString(),
    authorName: 'Аз',
    pending: 1 as const,
  };
  await db.messages.put(message);
  await enqueue('message', message.id, 'Съобщение');
  requestFlush();
}

/** ─── Проверка на буса ────────────────────────────────────────────────── */

export async function saveVehicleCheck(input: {
  tripId: string | null;
  vehicleId: string | null;
  odometerKm: number | null;
  items: VehicleCheckItem[];
  problem: string | null;
}): Promise<void> {
  const check = {
    id: newId('chk'),
    tripId: input.tripId,
    vehicleId: input.vehicleId,
    at: new Date().toISOString(),
    odometerKm: input.odometerKm,
    items: input.items,
    problem: input.problem,
    synced: 0 as const,
  };
  await db.checks.put(check);
  await enqueue('vehicle_check', check.id, 'Проверка на буса');
  requestFlush();
}
